import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Login.css';
import { useT } from '@/i18n/useT';

export default function Login() {
    const t = useT();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPw, setShowPw] = useState(false);
    const [remember, setRemember] = useState(true);
    const [error, setError] = useState('');
    const { login, loading } = useAuth();
    const navigate = useNavigate();
    const trainRef = useRef<SVGCircleElement | null>(null);
    const svgRef = useRef<SVGSVGElement | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        try {
            await login(email, password);
            navigate('/dashboard');
        } catch {
            setError(t('localizacion.login.credencialesInvalidas'));
        }
    };

    // Animated train: hops between routes that all pass through the central
    // interchange, picking a random one (forward or reversed) each cycle.
    useEffect(() => {
        const train = trainRef.current;
        const svg = svgRef.current;
        if (!train || !svg) return;

        const routes = [
            { d: 'M 90 120 Q 130 200 180 280 T 280 460 L 360 540 L 460 600 L 540 680', color: '#D8434A', duration: 9000 },
            { d: 'M 80 380 Q 180 420 280 460 L 380 360 L 460 280', color: '#4A8FCC', duration: 7500 },
            { d: 'M 280 460 L 360 600 L 440 720', color: '#E8A23B', duration: 5000 },
        ];

        // Reversed variants are sampled geometrically so they trace the exact
        // same curve as the forward path (no "derailing" on shorthand curves).
        const reversePath = (d: string): string => {
            const tmp = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            tmp.setAttribute('d', d);
            svg.appendChild(tmp);
            const len = tmp.getTotalLength();
            const steps = Math.max(40, Math.round(len / 8));
            const pts: string[] = [];
            for (let i = steps; i >= 0; i--) {
                const p = tmp.getPointAtLength((i / steps) * len);
                pts.push(`${p.x.toFixed(1)} ${p.y.toFixed(1)}`);
            }
            svg.removeChild(tmp);
            return 'M ' + pts.join(' L ');
        };

        const allRoutes = routes.concat(
            routes.map((r) => ({ d: reversePath(r.d), color: r.color, duration: r.duration })),
        );

        let prev = -1;
        let timer: ReturnType<typeof setTimeout>;
        let cancelled = false;

        const pick = () => {
            let i;
            do {
                i = Math.floor(Math.random() * allRoutes.length);
            } while (i === prev && allRoutes.length > 1);
            prev = i;
            return allRoutes[i];
        };

        const run = () => {
            if (cancelled) return;
            const r = pick();
            const s = train.style as CSSStyleDeclaration & { offsetPath?: string; offsetDistance?: string };
            s.offsetPath = `path('${r.d}')`;
            s.fill = r.color;
            s.filter = `drop-shadow(0 0 6px ${r.color})`;
            s.transition = 'none';
            s.offsetDistance = '0%';
            train.getBoundingClientRect(); // force reflow
            s.transition = `offset-distance ${r.duration}ms linear`;
            s.offsetDistance = '100%';
            timer = setTimeout(run, r.duration + 250);
        };

        timer = setTimeout(run, 400);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, []);

    return (
        <div className="ms-login">
            {/* Floating form card on top of the full-bleed visual */}
            <section className="ms-login__form">
                <div className="ms-login__brand">
                    <img src="/icons/logo_completo.png" alt="MetroSafe" className="ms-login__brand-logo" />
                </div>

                <div className="ms-login__inner">
                    <div className="ms-login__eyebrow"><span className="dot" />{t('localizacion.login.eyebrow')}</div>
                    <h1 className="ms-login__title">{t('localizacion.login.titulo')}</h1>
                    <p className="ms-login__sub">{t('localizacion.login.subtitulo')}</p>

                    <form onSubmit={handleSubmit}>
                        {error && <div className="ms-login__error">{error}</div>}

                        <div className="field">
                            <label htmlFor="login-email">{t('localizacion.login.email')}</label>
                            <div className="field__wrap">
                                <svg className="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="2" y="4" width="20" height="16" rx="2" />
                                    <path d="m22 7-10 6L2 7" />
                                </svg>
                                <input
                                    id="login-email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder={t('localizacion.login.emailPlaceholder')}
                                    autoComplete="email"
                                    required
                                />
                            </div>
                        </div>

                        <div className="field">
                            <label htmlFor="login-password">{t('localizacion.login.password')}</label>
                            <div className="field__wrap">
                                <svg className="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="11" width="18" height="11" rx="2" />
                                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                </svg>
                                <input
                                    id="login-password"
                                    type={showPw ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••••"
                                    autoComplete="current-password"
                                    required
                                />
                                <button
                                    type="button"
                                    className="field__toggle"
                                    onClick={() => setShowPw((v) => !v)}
                                    aria-label={t(showPw ? 'localizacion.login.ocultarPassword' : 'localizacion.login.mostrarPassword')}
                                    title={t('localizacion.login.mostrarOcultar')}
                                >
                                    {showPw ? (
                                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
                                            <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
                                            <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
                                            <line x1="2" y1="2" x2="22" y2="22" />
                                        </svg>
                                    ) : (
                                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                                            <circle cx="12" cy="12" r="3" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>

                        <div className="row">
                            <label className="check">
                                <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
                                <span className="box" />
                                {t('localizacion.login.mantenerSesion')}
                            </label>
                            <a href="#" className="link">{t('localizacion.login.olvidastePassword')}</a>
                        </div>

                        <button type="submit" className="submit" disabled={loading}>
                            {t(loading ? 'localizacion.login.iniciando' : 'localizacion.login.iniciar')}
                            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M5 12h14M13 5l7 7-7 7" />
                            </svg>
                        </button>

                        <div className="divider">{t('localizacion.login.o')}</div>

                        <button type="button" className="sso">
                            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="3" width="7" height="7" rx="1" />
                                <rect x="14" y="3" width="7" height="7" rx="1" />
                                <rect x="3" y="14" width="7" height="7" rx="1" />
                                <rect x="14" y="14" width="7" height="7" rx="1" />
                            </svg>
                            {t('localizacion.login.sso')}
                        </button>
                    </form>
                </div>

                <div className="ms-login__foot">
                    <span>© 2026 MetroSafe · v2.4.1</span>
                    <span><a href="#">{t('localizacion.login.soporte')}</a> · <a href="#">{t('localizacion.login.privacidad')}</a></span>
                </div>
            </section>

            {/* Full-bleed animated visual panel (background) */}
            <aside className="ms-login__visual">
                <svg ref={svgRef} className="map-svg" viewBox="0 0 600 800" preserveAspectRatio="xMidYMid slice">
                    <path className="map-line map-line--l1" d="M 90 120 Q 130 200 180 280 T 280 460 L 360 540 L 460 600 L 540 680" />
                    <path className="map-line map-line--l2" d="M 80 380 Q 180 420 280 460 L 380 360 L 460 280" />
                    <path className="map-line map-line--l3" d="M 280 460 L 360 600 L 440 720" />

                    <g>
                        <circle className="map-station" cx="90" cy="120" r="5" />
                        <circle className="map-station" cx="180" cy="280" r="5" />
                        <circle className="map-station" cx="280" cy="460" r="6" />
                        <circle className="map-station" cx="360" cy="540" r="5" />
                        <circle className="map-station" cx="460" cy="600" r="5" />
                        <circle className="map-station" cx="540" cy="680" r="5" />
                        <circle className="map-station" cx="380" cy="360" r="5" />
                        <circle className="map-station" cx="460" cy="280" r="5" />
                        <circle className="map-station" cx="360" cy="600" r="5" />
                        <circle className="map-station" cx="440" cy="720" r="5" />
                        <circle className="map-station" cx="80" cy="380" r="5" />
                    </g>

                    <circle className="map-pulse" cx="280" cy="460" r="6" />
                    <circle className="map-pulse" cx="280" cy="460" r="6" style={{ animationDelay: '1.3s' }} />

                    <circle
                        ref={trainRef}
                        className="map-train"
                        r="4"
                        style={{ offsetDistance: '0%' } as React.CSSProperties}
                    />
                </svg>

                <div className="visual__head">
                    <span className="visual__live">
                        <span className="visual__live-dot" />
                        {t('localizacion.login.sistemaOperativo')}
                    </span>
                </div>

                <div className="visual__caption">
                    <h2>{t('localizacion.login.visualTitulo')}</h2>
                    <p>{t('localizacion.login.visualTexto')}</p>
                </div>

                <div className="visual__stats">
                    <div>
                        <div className="visual__stat-label">{t('localizacion.login.agentesActivos')}</div>
                        <div className="visual__stat-value">17<span className="visual__stat-frac">/20</span></div>
                    </div>
                    <div>
                        <div className="visual__stat-label">{t('localizacion.login.coberturaBle')}</div>
                        <div className="visual__stat-value">92%</div>
                    </div>
                    <div>
                        <div className="visual__stat-label">{t('localizacion.login.latenciaMedia')}</div>
                        <div className="visual__stat-value visual__stat-value--accent">142<span className="visual__stat-unit"> ms</span></div>
                    </div>
                </div>
            </aside>
        </div>
    );
}
